/**
 * LabPanel — top-level "Lab" tab content.
 *
 * Flat, reverse-chronological gallery of agent-authored HTML prototype
 * documents (see sync_lab_asset_to_production). Agents publish one or
 * more standalone HTML docs at any point in a project's lifecycle —
 * e.g. multiple options for an interactive experience before it gets
 * built for real — and this is where the user reviews them. Clicking a
 * card opens LabAssetViewer full-screen, mirroring the Studio tab's
 * slide-viewing UX.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { FlaskConical, Loader2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { labAssetsAPI, type LabAsset } from '@/services/api';
import { LabAssetViewer } from './LabAssetViewer';

interface LabPanelProps {
  conversationId: string;
}

export const LabPanel: React.FC<LabPanelProps> = ({ conversationId }) => {
  const [assets, setAssets] = useState<LabAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    try {
      const { assets } = await labAssetsAPI.get(conversationId);
      setAssets(assets);
    } catch (err) {
      console.error('[LabPanel] Failed to load lab assets', err);
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  useEffect(() => {
    setLoading(true);
    refetch();
  }, [refetch]);

  useEffect(() => {
    const onUpdate = (e: Event) => {
      if ((e as CustomEvent).detail?.conversationId === conversationId) refetch();
    };
    window.addEventListener('lab-assets-updated', onUpdate);
    return () => window.removeEventListener('lab-assets-updated', onUpdate);
  }, [conversationId, refetch]);

  const selectedAsset = assets.find((a) => a.slug === selectedSlug) || null;

  if (selectedAsset) {
    return (
      <LabAssetViewer
        conversationId={conversationId}
        asset={selectedAsset}
        onBack={() => setSelectedSlug(null)}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex-1 min-h-0 flex items-center justify-center text-gray-400">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  if (assets.length === 0) {
    return (
      <div className="flex-1 min-h-0 flex flex-col items-center justify-center text-center px-8">
        <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-zinc-800 flex items-center justify-center mb-4">
          <FlaskConical className="w-8 h-8 text-gray-400 dark:text-zinc-500" />
        </div>
        <h3 className="text-base font-medium text-gray-600 dark:text-zinc-400 mb-1">No lab assets yet</h3>
        <p className="text-sm text-gray-400 dark:text-zinc-500 max-w-[320px]">
          Ask the agent to prototype an experience — it can publish one or more HTML options here for you to review.
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 overflow-y-auto p-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {assets.map((asset) => (
          <button
            key={asset.id}
            onClick={() => setSelectedSlug(asset.slug)}
            className="text-left rounded-xl border border-black/10 dark:border-white/10 bg-white dark:bg-zinc-900 overflow-hidden hover:shadow-lg hover:border-black/20 dark:hover:border-white/20 transition-all"
          >
            <div className="aspect-video bg-gray-50 dark:bg-zinc-800 relative overflow-hidden">
              {asset.viewUrl && (
                <iframe
                  src={asset.viewUrl}
                  title={asset.title}
                  sandbox="allow-scripts allow-same-origin"
                  className="w-full h-full border-0 pointer-events-none"
                  tabIndex={-1}
                />
              )}
            </div>
            <div className="p-3">
              <div className="text-sm font-medium truncate">{asset.title}</div>
              {asset.description && (
                <div className="text-xs text-gray-500 dark:text-zinc-400 truncate mt-0.5">{asset.description}</div>
              )}
              <div className="text-[11px] text-gray-400 dark:text-zinc-500 mt-1.5">
                v{asset.currentVersion} · updated {formatDistanceToNow(new Date(asset.updatedAt), { addSuffix: true })}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default LabPanel;
